import { Matrix4 } from 'three';
import { getOrThrow } from '../../../../../../cross-team-shared/misc-utils/strict-record';
import { ViewBox } from '../../../../../../team-apis/content/view-box';
import { ProjectionCamera } from '../../../../../renderers/projection/projection-camera';
import { PointCloudContentState } from '../../../../../state/point-cloud-content-state';
import { getSplitViewOutputNameOrThrow } from '../../../../../utils/viewbox-highlight-utils';
import { CloudShaderMaterial } from '../../../cloud-shader-material';

export interface ViewBoxHighlightUniforms {
  splitCameraProjectionMatrix: Matrix4;
  splitCameraViewMatrix: Matrix4;
  viewBox: ViewBox;
}

export const viewBoxHighlightUniforms = `
  struct ViewBoxHighlightUniforms {
    mat4 splitCameraProjectionMatrix;
    mat4 splitCameraViewMatrix;
    vec4 viewBox;
  };
`;

export const viewBoxHighlight = `
  bool isViewBoxHighlightEnabled() {
    vec4 viewBox = color.viewBoxHighlight.viewBox;
    vec4 invalidViewBox = vec4(0.);
    return !all(equal(viewBox, invalidViewBox));
  }

  bool isPointVisibleInSplitView() {
    mat4 splitCameraProjectionMatrix = color.viewBoxHighlight.splitCameraProjectionMatrix;
    mat4 splitCameraViewMatrix = color.viewBoxHighlight.splitCameraViewMatrix;
    vec4 viewBox = color.viewBoxHighlight.viewBox;

    // Project the 3D point to clip space
    vec4 clipSpacePosition = getProjection(splitCameraProjectionMatrix, splitCameraViewMatrix);

    // if the projected point is behind the camera
    if(clipSpacePosition.w <= 0.0 || clipSpacePosition.z < -1.0){
      return false;
    }

    vec3 ndc = clipSpacePosition.xyz / clipSpacePosition.w;

    // Map NDC to image coordinates
    vec2 projectedInImage = ((ndc.xy + 1.0) * 0.5) * imageSize;

    // Check if the projected point lies within the view box
    float minX = viewBox.x;
    float maxX = viewBox.x + viewBox.z;

    return projectedInImage.x >= minX &&
            projectedInImage.x < maxX &&
            projectedInImage.x <= imageSize.x &&
            projectedInImage.x >= 0.0 &&
            projectedInImage.y <= imageSize.y &&
            projectedInImage.y >= 0.0;
  }
`;

export function setViewBoxHighlightUniforms(
  material: CloudShaderMaterial,
  state?: PointCloudContentState,
  splitViewCamera?: ProjectionCamera,
) {
  let uniforms: ViewBoxHighlightUniforms;

  if (state && splitViewCamera) {
    const splitViewOutputName = getSplitViewOutputNameOrThrow(state);
    uniforms = {
      splitCameraProjectionMatrix: splitViewCamera.projectionMatrix,
      splitCameraViewMatrix: splitViewCamera.matrixWorldInverse,
      viewBox: getOrThrow(state.viewBoxes, splitViewOutputName),
    };
  } else {
    uniforms = {
      splitCameraProjectionMatrix: new Matrix4(),
      splitCameraViewMatrix: new Matrix4(),
      viewBox: invalidViewBox,
    };
  }

  material.setColorUniform('viewBoxHighlight', uniforms);
}

export const invalidViewBox: ViewBox = [0, 0, 0, 0];
